import React, { useEffect, useRef } from "react";
import { Box, Container, Typography } from "@mui/material";
import { z } from "zod";
import { Form, useLocation, useNavigate, useParams } from "react-router-dom";
import { apiClient, apiClientHooks } from "../bootstrapping/InitApiClient";
import { CONSENT_PAGE_VERSION } from "../consentVersions";
import { Helmet } from "react-helmet-async";
import { queryClient } from "../bootstrapping/InitReactQuery";
import FormButtons from "../components/FormButtons";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckboxElement } from "react-hook-form-mui";
import TestApplicationAlert from "../components/TestApplicationAlert";

export const useRedirectToGDPRConsentIfRequired = (eBulkApplicationID: string) => {
  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: eBulkApplicationID } })
  const isRedirecting = !getEBulkApplicationByID.isLoading && getEBulkApplicationByID.data?.gdpr_consent === false
  const navigate = useNavigate()
  const location = useLocation()

  // Redirect to the GDPR Consent page if the user has not consented
  useEffect(() => {
    if (isRedirecting) {
      navigate(`/applications/${eBulkApplicationID}/gdpr`, { state: { from: location.pathname } })
    }
  }, [isRedirecting, eBulkApplicationID, navigate, location.pathname])

  return isRedirecting
}

// Three separate captures, each with its own tick box (DBS inspection feedback, DBS-337): Total Screening's statement of
// fair processing (for us), the DBS privacy notice declaration (DBS-mandated wording, DBS-330) and the three e-Bulk
// e-result consent statements (DBS e-Bulk wording). Every box is required before the applicant can continue.
const zGDPRForm = z.object({
  fairProcessing: z.boolean(),
  privacyNotice: z.boolean(),
  consent1: z.boolean(),
  consent2: z.boolean(),
  consent3: z.boolean(),
})
type GDPRForm = z.infer<typeof zGDPRForm>
const CONSENT_FIELDS = ['fairProcessing', 'privacyNotice', 'consent1', 'consent2', 'consent3'] as const
const CONSENT_MESSAGES: Record<keyof GDPRForm, string> = {
  fairProcessing: 'You must agree to the statement of fair processing to proceed',
  privacyNotice: 'You must confirm you have read the DBS privacy notice to proceed',
  consent1: 'You must consent to proceed',
  consent2: 'You must confirm you understand to proceed',
  consent3: 'You must confirm you understand to proceed',
}

export const Element: React.FC = () => {
  const { application_id } = useParams<{ application_id?: string }>()
  if (!application_id) throw new Error('application_id is required')

  const form = useForm<GDPRForm>({
    resolver: zodResolver(
      zGDPRForm
        .superRefine((values, context) => {
          for (const field of CONSENT_FIELDS) {
            if (!values[field]) context.addIssue({ code: 'custom', message: CONSENT_MESSAGES[field], path: [field] })
          }
        })
    ),
    mode: 'onBlur',
    defaultValues: {
      fairProcessing: false,
      privacyNotice: false,
      consent1: false,
      consent2: false,
      consent3: false,
    },
  })

  const navigate = useNavigate()
  const location = useLocation()

  const consentMyEBulkApplicationByID = apiClientHooks.useConsentMyEBulkApplicationByID({ params: { id: application_id } })
  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: application_id } })
  const gdpr_consent = getEBulkApplicationByID.data?.gdpr_consent

  useEffect(() => {
    console.debug('GDPR Consent', { gdpr_consent })

    // Once we consent to GDPR, we move on automatically
    if (gdpr_consent === true) {
      console.debug('Consent for GDPR granted!', { id: application_id })

      // If we were told where we came from, go there, otherwise go back to the application list
      // The state is sent in `useRedirectToGDPRConsentIfRequired`
      const lastPage = z.string().safeParse(location.state && location.state.from)
      const nextPage = lastPage.success ? lastPage.data : `/`

      console.debug('Redirecting to', nextPage)
      navigate(nextPage)
    }
  }, [gdpr_consent, location, application_id, navigate])

  // Unfortuantely we can't use the formState.touched property, as cleared when a checkbox returns to it's original state
  const touched = useRef<Partial<Record<keyof GDPRForm, boolean>>>({})

  // Show / hide the errors whenever the consents change (but only once the user has interacted with the form)
  const values = form.watch()
  const ticked = CONSENT_FIELDS.map(field => String(values[field])).join(',')
  useEffect(() => {
    for (const field of CONSENT_FIELDS) if (touched.current[field]) form.trigger(field)
  }, [form, ticked])

  /** One tick box with its statement: the box sits beside the first line of the statement, not mid-paragraph */
  const tick = (name: keyof GDPRForm, label: React.ReactNode) =>
    <CheckboxElement
      control={form.control}
      name={name}
      label={label}
      onClick={() => touched.current[name] = true}
      sx={{ marginTop: '-9px' }}
      labelProps={{ sx: { alignItems: 'flex-start', alignSelf: 'flex-start', marginBottom: 1, marginRight: 0 } }}
    />

  return <>
    <Helmet>
      <title>Statement of Fair Processing</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Form method="post">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginBottom: 3 }} />
          <Typography component="h1" variant="h5" marginBottom={3}>
            Statement of Fair Processing
          </Typography>
          <Typography variant="body1" marginBottom={1}>
            <strong>Please read the statement of fair processing below, then tick each box to confirm.</strong>
          </Typography>
          <Typography variant="body1" marginBottom={1}>
            Applications for <strong>Standard and Enhanced Checks</strong> are processed by Disclosure and Barring Service (DBS).
          </Typography>
          <Typography variant="body1" marginBottom={1}>
            By accessing the Website and providing your personal details, you agree to accept and be bound by the Privacy Policy which explains how {process.env.REACT_APP_DATA_CONTROLLERS} processes your data for the purposes of obtaining your Disclosure from DBS, the key terms of which are non-exhaustively summarised in this statement of fair processing.
          </Typography>
          <Typography variant="body1" marginBottom={1}>
            Data can only be amended by the applicant using the email address and password supplied at registration. Therefore, it is important that you keep this information in a secure place.
          </Typography>
          {tick('fairProcessing', 'I have read and agree to the statement of fair processing above.')}

          <Typography component="h2" variant="h6" marginTop={2} marginBottom={1} sx={{ alignSelf: 'flex-start' }}>
            DBS Privacy Notice
          </Typography>
          {tick('privacyNotice', <>I have read the Privacy notice for DBS Disclosure Services <a href="https://www.gov.uk/government/publications/dbs-disclosure-services-privacy-notice" target="_blank" rel="noopener noreferrer">DBS Disclosure Services Privacy Notice - GOV.UK</a> and I understand how DBS will process my personal data and the options available to me for submitting an application</>)}

          <Typography component="h2" variant="h6" marginTop={2} marginBottom={1} sx={{ alignSelf: 'flex-start' }}>
            Consent to obtain e-Bulk Standard/Enhanced Check electronic result
          </Typography>
          {tick('consent1', "I consent to the DBS providing an electronic result directly to the registered body that has submitted my application. I understand that an electronic result contains a message that indicates either the certificate is blank or to await certificate which will indicate that my certificate contains criminal record information. In some cases the registered body may provide this information directly to my employer prior to me receiving my certificate.")}
          {tick('consent2', "I understand if I do not consent to an electronic result being issued to the registered body submitting my application that I must not proceed with this application and I should submit a paper application form.")}
          {tick('consent3', "I understand that to withdraw my consent whilst my application is in progress I must contact the DBS helpline 03000 200 190.  My application will then be withdrawn.")}
        </Box>
        <FormButtons
          form={form}
          showHomeButton={false}
          nextButtonProps={{
            onClick: async event => {
              const isValid = await form.trigger();
              if (!isValid) {
                console.warn("Form is not valid", { isValid, errors: form.formState.errors });
                event.preventDefault()
                return
              }

              console.debug('Consenting to GDPR', { id: application_id })
              // Record what was ticked, under the wording this build shows (DBS-266), then set the consent flag
              await apiClient.recordMyEBulkApplicationConsents(
                { screen: 'consent_page', wording_version: CONSENT_PAGE_VERSION, kinds: ['fair_processing', 'privacy_notice', 'eresult'] },
                { params: { id: application_id } },
              )
              await consentMyEBulkApplicationByID.mutateAsync(undefined)

              // Clear the cache for the application list and the current application, so we update the gdpr_consent status
              queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyEBulkApplications') })
              queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplicationByID') })
            }
          }}
        />
      </Form>
    </Container>
  </>
}

export default Element
